import cx from 'classnames'
import { motion } from 'framer-motion'
import { forwardRef, useContext, useEffect, useState } from 'react'

import { type SanityProductFragment } from '@data/sanity/queries/types/product'
import { AnalyticsEventName } from '@lib/analytics'
import { AnalyticsContext } from '@lib/analytics-context'
import { itemAnimation } from '@lib/animate'
import { CartContext } from '@lib/cart/context'
import { getPriceNumber } from '@lib/format'
import { type Filter } from '@lib/product'
import { getFilteredProductVariant } from '@lib/product-card'
import { UrlParameters, getLinkPageUrl } from '@lib/routes'
import { ShopContext } from '@lib/shop-context'
import { StringsContext } from '@lib/strings-context'

import ProductAddToCart from '@blocks/product/product-add-to-cart'
import ProductGallery from '@blocks/product/product-gallery'
import ProductOption from '@blocks/product/product-option'
import ProductPrice from '@blocks/product/product-price'
import ProductThumbnail from '@blocks/product/product-thumbnail'
import { ButtonColor, ButtonVariant } from '@components/buttons/button'
import SimpleLink from '@components/simple-link'

interface ProductCardProps {
  product: SanityProductFragment
  hasVisuals?: boolean
  showGallery?: boolean
  showThumbnails?: boolean
  showPrice?: boolean
  showOption?: boolean
  showQuickAdd?: boolean
  activeFilters?: Filter[]
  isInline?: boolean
  isFeatured?: boolean
  className?: string
  onClick?: () => void
}

const ProductCard = forwardRef<HTMLDivElement, ProductCardProps>(
  (
    {
      product,
      hasVisuals,
      showGallery,
      showThumbnails,
      showPrice,
      showOption,
      showQuickAdd,
      activeFilters,
      isInline,
      isFeatured,
      className,
      onClick,
    },
    ref,
  ) => {
    const strings = useContext(StringsContext)
    const { openCartAfterAddingItems, addItemsToCart, toggleCart } =
      useContext(CartContext)
    const { triggerAnalyticsEvent } = useContext(AnalyticsContext)
    const { currencyCode } = useContext(ShopContext)

    const [activeVariant, setActiveVariant] = useState(() =>
      getFilteredProductVariant(product, activeFilters ?? []),
    )

    useEffect(
      () =>
        setActiveVariant(
          getFilteredProductVariant(product, activeFilters ?? []),
        ),
      [activeFilters, product],
    )

    const galleryPhotos = product.galleryPhotos ?? []
    const listingPhotos = product.listingPhotos ?? []
    const hasGallery = showGallery && galleryPhotos.length > 0
    const hasThumbnails = showThumbnails && listingPhotos.length > 0
    const hasQuickAdd = showQuickAdd && !!activeVariant?.inStock

    // Get product URL
    const parameters: UrlParameters = {
      variant:
        product.surfaceOption && activeVariant
          ? `${activeVariant.variantID}`
          : '',
    }
    const productUrl = getLinkPageUrl(
      'product',
      product.slug.current,
      parameters,
    )

    const handleAddToCart = async () => {
      if (!activeVariant) {
        return
      }

      const quantity = 1
      const isSuccessful = await addItemsToCart([
        {
          id: activeVariant.variantID,
          quantity,
        },
      ])

      if (isSuccessful) {
        triggerAnalyticsEvent(AnalyticsEventName.AddToCart, {
          currency: currencyCode,
          value: getPriceNumber(activeVariant.price * quantity),
          items: [
            {
              index: 0,
              item_id: activeVariant.variantID,
              item_name: product.title,
              item_variant: activeVariant.options
                ?.map((option) => option.value)
                ?.join(', '),
              price: getPriceNumber(activeVariant.price),
              discount: activeVariant.comparePrice
                ? getPriceNumber(
                    activeVariant.comparePrice - activeVariant.price,
                  )
                : 0,
              quantity,
            },
          ],
        })

        if (openCartAfterAddingItems) {
          toggleCart(true)
        }
      }
    }

    return (
      <motion.div
        ref={ref}
        variants={itemAnimation}
        className={cx(
          'flex flex-col relative group',
          {
            'my-4': isInline,
          },
          className,
        )}
      >
        {hasVisuals && activeVariant && (hasGallery || hasThumbnails) && (
          <div className="relative">
            {hasGallery && (
              <div className="relative">
                <ProductGallery
                  photosets={galleryPhotos}
                  activeVariant={activeVariant}
                  hasArrows
                  hasDots
                  hasDrag={false}
                />
              </div>
            )}

            {hasThumbnails && (
              <div className="z-0">
                <ProductThumbnail
                  galleryPhotos={galleryPhotos}
                  listingPhotos={listingPhotos}
                  activeVariant={activeVariant}
                />
              </div>
            )}

            {hasQuickAdd && (
              <div
                className={cx(
                  'absolute bottom-0 inset-x-0 z-30 text-center mx-4 mb-8 opacity-0 invisible translate-y-1/2 transition-all will-change-transform',
                  'group-hover:opacity-100 group-hover:visible group-hover:translate-y-0',
                )}
              >
                <ProductAddToCart
                  variant={ButtonVariant.FILLED}
                  color={ButtonColor.DEFAULT}
                  onClick={handleAddToCart}
                >
                  {strings.buttonAddToCart}
                </ProductAddToCart>
              </div>
            )}
          </div>
        )}

        <div
          className={cx('text-center', {
            'mt-4': isInline,
            'mt-6': !isInline,
          })}
        >
          <div>
            <h2
              className={cx('!m-0', {
                'text-2xl': isInline,
                'text-base': isFeatured,
              })}
            >
              <SimpleLink
                className="block no-underline text-current after:block after:absolute after:inset-0 after:z-20"
                href={productUrl}
                onClick={onClick}
                onBeforeInput={onClick}
                tabIndex={0}
              >
                {product.title}
              </SimpleLink>
            </h2>

            {showPrice && (
              <ProductPrice
                className="flex items-center justify-center mt-2"
                price={activeVariant?.price ?? product.price}
                comparePrice={
                  activeVariant?.comparePrice ?? product.comparePrice
                }
                inProductCard
              />
            )}
          </div>

          {showOption && activeVariant && (
            <div className="relative z-30 mt-4">
              {product.options.map((option) => {
                if (
                  !product.surfaceOption ||
                  option.position !== parseInt(product.surfaceOption) ||
                  option.values.length <= 1
                ) {
                  return null
                }

                return (
                  <ProductOption
                    key={option.name}
                    option={option}
                    optionSettings={product.optionSettings ?? []}
                    variants={product.variants ?? []}
                    activeVariant={activeVariant}
                    strictMatch={false}
                    hideLabels
                    onChange={setActiveVariant}
                    inProductCard
                  />
                )
              })}
            </div>
          )}
        </div>
      </motion.div>
    )
  },
)

ProductCard.displayName = 'ProductCard'

export default ProductCard
